'use client';

import { useCallback, useEffect } from 'react';
import { MESSAGES, type Message } from '@/utils/message-broker/types';
import { MessageBroker } from '@/utils/message-broker';
import { useStore } from '@/store/store';
import { getRecentlyPlayedZone } from '@/logic/games/recently-played-games';
import type { Game } from '@/types/games';

/**
 * Keeps `userContext.recentGames` in sync with the backend recently-played list on pages
 * that need the signal but do not render a Recently Played zone (e.g. the New category).
 */
export const useRecentGamesSync = () => {
    const setRecentGames = useStore((state) => state.userContext.setRecentGames);

    const refresh = useCallback(async () => {
        const zone = await getRecentlyPlayedZone();

        if (!zone) {
            return;
        }

        const items = (zone.data.body[0]?.items ?? []) as Game[];
        setRecentGames(items.map((game) => game.uid));
    }, [setRecentGames]);

    useEffect(() => {
        const onRefresh = (message: Message) => {
            if (message.data.type !== MESSAGES.REFRESH_RECENTLY_PLAYED) {
                return;
            }

            void refresh();
        };

        MessageBroker.subscribe(onRefresh);

        return () => MessageBroker.unsubscribe(onRefresh);
    }, [refresh]);
};
